import React, { useState, useEffect } from "react";
import { Typography } from "antd";
import styled, { keyframes } from "styled-components";
import { ClockCircleOutlined } from "@ant-design/icons";
import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";

const { Title } = Typography;

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.6; }
  100% { opacity: 1; }
`;

const HeaderContainer = styled.div<{ isMobile?: boolean }>`
  display: flex;
  flex-direction: ${(props) => (props.isMobile ? "column" : "row")};
  justify-content: space-between;
  align-items: ${(props) => (props.isMobile ? "flex-start" : "center")};
  gap: ${(props) => (props.isMobile ? "12px" : "0px")};
  margin-bottom: 24px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 16px;

  @media (max-width: 768px) {
    margin-bottom: 16px;
    padding-bottom: 12px;
  }
`;

const ScoreBadge = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;

  .current {
    font-size: 24px;
    font-weight: 800;
    color: #1890ff;
    @media (max-width: 768px) {
      font-size: 20px;
    }
  }
  .total {
    font-size: 14px;
    font-weight: 600;
    color: #8c8c8c;
    @media (max-width: 768px) {
      font-size: 12px;
    }
  }
`;

const TimerContainer = styled.div<{ isUrgent: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: ${(props) => (props.isUrgent ? "#fff2f0" : "#f6ffed")};
  border: 1px solid ${(props) => (props.isUrgent ? "#ffccc7" : "#b7eb8f")};
  border-radius: 20px;
  color: ${(props) => (props.isUrgent ? "#ff4d4f" : "#52c41a")};
  font-weight: 600;
  font-size: 14px;
  animation: ${(props) => (props.isUrgent ? pulse : "none")} 2s infinite
    ease-in-out;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);

  .clock-icon {
    font-size: 16px;
  }
`;

interface EvaluationHeaderProps {
  totalScore: number;
  maxTotalScore: number;
  onTimeUp?: () => void;
  initialTimeInSeconds?: number;
  obtainedMarks?: number | null;
}

const EvaluationHeader: React.FC<EvaluationHeaderProps> = ({
  maxTotalScore,
  onTimeUp,
  initialTimeInSeconds = 0,
  obtainedMarks,
}) => {
  const { screen }: any = useWindowSize();
  const isMobile = ["sm", "md"].includes(screen);
  const [timeLeft, setTimeLeft] = useState(initialTimeInSeconds);
  const [hasFiredTimeUp, setHasFiredTimeUp] = useState(false);

  useEffect(() => {
    setTimeLeft(initialTimeInSeconds);
    setHasFiredTimeUp(false);
  }, [initialTimeInSeconds]);

  useEffect(() => {
    if (initialTimeInSeconds <= 0) return;

    if (timeLeft <= 0) {
      if (onTimeUp && !hasFiredTimeUp) {
        setHasFiredTimeUp(true);
        onTimeUp();
      }
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, onTimeUp, hasFiredTimeUp, initialTimeInSeconds]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const isUrgent = timeLeft < 300; // Less than 5 minutes

  return (
    <HeaderContainer isMobile={isMobile}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          width: isMobile ? "100%" : "auto",
        }}
      >
        <Title
          level={4}
          style={{
            margin: 0,
            color: "#262626",
            fontSize: isMobile ? "12px" : "13px",
            fontWeight: 700,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
          }}
        >
          Evaluation Progress
        </Title>
        {initialTimeInSeconds > 0 && (
          <TimerContainer isUrgent={isUrgent}>
            <ClockCircleOutlined className="clock-icon" />
            <span>{formatTime(timeLeft)}</span>
            <span style={{ fontSize: "11px", fontWeight: 500, opacity: 0.8 }}>
              Remaining
            </span>
          </TimerContainer>
        )}
      </div>
      <ScoreBadge>
        <span
          style={{
            color: "#8c8c8c",
            fontSize: isMobile ? "12px" : "14px",
            fontWeight: 500,
          }}
        >
          Total Marks:
        </span>
        {obtainedMarks !== undefined && obtainedMarks !== null ? (
          <>
            <span className="current">{obtainedMarks}</span>
            <span className="total">/ {maxTotalScore}</span>
          </>
        ) : (
          <span className="current">{maxTotalScore}</span>
        )}
      </ScoreBadge>
    </HeaderContainer>
  );
};

export default EvaluationHeader;
